import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { UserPlus, Target } from "lucide-react";
import { GET_MORE_AUDITIONS_TEMPLATE } from "@/constants/getMoreAuditionsTemplate";

/**
 * Renders the static "Get More Auditions in the UK" template content (10 steps)
 * inside the existing GoalDetail page when goal.template === "get_more_auditions".
 *
 * Persists per-goal user answers via the existing PATCH /api/career/goals/{id}
 * endpoint (template_data field). No new backend endpoint, no LLM call.
 */
export default function GetMoreAuditionsGoalContent({ goal, lang, onGoalUpdated }) {
  const L = GET_MORE_AUDITIONS_TEMPLATE[lang === "en" ? "en" : "pl"];

  // Persisted state buckets — mirror the shape stored in goal.template_data.
  const [step1, setStep1] = useState({});      // numeric fields keyed by field.key
  const [step2, setStep2] = useState({});      // text fields keyed by field.key
  const [step3, setStep3] = useState({});      // checklist by index
  const [step8, setStep8] = useState({});      // checklist by index
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const bootRef = useRef(false);

  useEffect(() => {
    const td = goal?.template_data || {};
    setStep1(td.step1 || {});
    setStep2(td.step2 || {});
    setStep3(td.step3 || {});
    setStep8(td.step8 || {});
    bootRef.current = true;
  }, [goal?.goal_id]);

  const schedulePatch = (next1, next2, next3, next8) => {
    if (!bootRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSaving(true);
      try {
        const r = await api.patch(`/career/goals/${goal.goal_id}`, {
          template_data: { step1: next1, step2: next2, step3: next3, step8: next8 },
        });
        onGoalUpdated?.(r.data);
      } catch {
        // Silent: 401 handled by interceptor, other errors retry on next input.
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const onNumericChange = (key, value) => {
    setStep1((prev) => {
      const next = { ...prev, [key]: value };
      schedulePatch(next, step2, step3, step8);
      return next;
    });
  };
  const onFieldChange = (key, value) => {
    setStep2((prev) => {
      const next = { ...prev, [key]: value };
      schedulePatch(step1, next, step3, step8);
      return next;
    });
  };
  const onStep3Toggle = (i) => {
    setStep3((prev) => {
      const next = { ...prev, [i]: !prev[i] };
      schedulePatch(step1, step2, next, step8);
      return next;
    });
  };
  const onStep8Toggle = (i) => {
    setStep8((prev) => {
      const next = { ...prev, [i]: !prev[i] };
      schedulePatch(step1, step2, step3, next);
      return next;
    });
  };

  // --- Auto-computed rates for Step 1 --------------------------------------
  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };
  const rates = useMemo(() => {
    const agent = toNum(step1.agent_submissions);
    const self = toNum(step1.self_submissions);
    const auditions = toNum(step1.auditions);
    const callbacks = toNum(step1.callbacks);
    const bookings = toNum(step1.bookings);
    const total = agent + self;
    const fmt = (num, denom) => {
      if (!denom || denom <= 0) return null;
      return Math.round((num / denom) * 1000) / 10; // one decimal, %
    };
    return {
      total,
      auditionRate: fmt(auditions, total),
      callbackRate: fmt(callbacks, auditions),
      bookingRate: fmt(bookings, auditions),
    };
  }, [step1]);

  const displayRate = (v) => (v === null || v === undefined) ? L.ratesUnknown : `${v}%`;

  return (
    <div className="mt-2" data-testid="get-more-auditions-goal-content">
      <div className="flex items-center gap-2 mb-6">
        <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase flex items-center gap-1">
          <Target size={11} /> {L.eyebrow}
        </div>
        {saving && (
          <span className="mono text-[10px] uppercase tracking-widest text-stone-500">
            {L.savingLabel}
          </span>
        )}
      </div>

      {/* Intro */}
      <div className="paper p-6 border-amber-500/30 bg-amber-500/5">
        <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line">
          {L.intro}
        </p>
      </div>

      <div className="space-y-12 mt-10">
        {L.steps.map((step, idx) => (
          <motion.section
            key={idx}
            data-testid={`gma-step-${idx + 1}`}
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.05 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
              {L.stepLabel} {String(idx + 1).padStart(2, "0")}
            </div>
            <h2 className="serif text-2xl sm:text-3xl text-stone-100 mt-1">{step.title}</h2>

            {step.body && (
              <div className="text-stone-300 leading-relaxed mt-4 whitespace-pre-line">
                {step.body}
              </div>
            )}

            {/* Step 01 numeric fields + auto rates */}
            {step.numericFields && (
              <>
                <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {step.numericFields.map((f) => (
                    <li key={f.key} className="paper p-4">
                      <label className="serif text-base text-stone-100 block">{f.label}</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        data-testid={`gma-numeric-${f.key}`}
                        value={step1[f.key] ?? ""}
                        onChange={(e) => onNumericChange(f.key, e.target.value)}
                        className="script-input mono text-sm mt-3 w-full"
                        placeholder="0"
                      />
                    </li>
                  ))}
                </ul>

                <div className="mt-5 paper p-4">
                  <div className="mono text-[11px] tracking-[0.35em] text-amber-500 uppercase">
                    {L.step1RatesLabel}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                    <div>
                      <div className="text-stone-400 text-xs">{L.totalSubmissionsLabel}</div>
                      <div className="serif text-xl text-stone-100 mt-1" data-testid="gma-total-submissions">
                        {rates.total}
                      </div>
                    </div>
                    <div>
                      <div className="text-stone-400 text-xs">{L.auditionRateLabel}</div>
                      <div className="serif text-xl text-stone-100 mt-1" data-testid="gma-audition-rate">
                        {displayRate(rates.auditionRate)}
                      </div>
                    </div>
                    <div>
                      <div className="text-stone-400 text-xs">{L.callbackRateLabel}</div>
                      <div className="serif text-xl text-stone-100 mt-1" data-testid="gma-callback-rate">
                        {displayRate(rates.callbackRate)}
                      </div>
                    </div>
                    <div>
                      <div className="text-stone-400 text-xs">{L.bookingRateLabel}</div>
                      <div className="serif text-xl text-stone-100 mt-1" data-testid="gma-booking-rate">
                        {displayRate(rates.bookingRate)}
                      </div>
                    </div>
                  </div>
                </div>

                {step.guidance && (
                  <ul className="mt-5 space-y-2">
                    {step.guidance.map((g, i) => (
                      <li key={i} className="paper p-3 text-stone-200 text-sm leading-relaxed">
                        {g}
                      </li>
                    ))}
                  </ul>
                )}

                {step.effect && (
                  <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm">
                    {step.effect}
                  </div>
                )}
              </>
            )}

            {/* Step 02 text fields */}
            {step.fields && (
              <>
                <ul className="mt-5 space-y-3">
                  {step.fields.map((f) => (
                    <li key={f.key} className="paper p-4">
                      <label className="serif text-base text-stone-100 block">{f.label}</label>
                      {f.hint && <div className="text-stone-400 text-sm mt-1">{f.hint}</div>}
                      <textarea
                        data-testid={`gma-field-${f.key}`}
                        value={step2[f.key] || ""}
                        onChange={(e) => onFieldChange(f.key, e.target.value)}
                        rows={2}
                        placeholder={L.yourAnswerPlaceholder}
                        className="script-input mono text-sm mt-3 w-full"
                      />
                    </li>
                  ))}
                </ul>
                {step.after && (
                  <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm leading-relaxed whitespace-pre-line">
                    {step.after}
                  </div>
                )}
              </>
            )}

            {/* Steps 03 + 08 checklists */}
            {step.checklist && (
              <>
                <ul className="mt-5 space-y-2">
                  {step.checklist.map((label, i) => {
                    const stateBucket = idx === 2 ? step3 : step8;
                    const onToggle = idx === 2 ? onStep3Toggle : onStep8Toggle;
                    return (
                      <li key={i}>
                        <label className="paper p-3 flex items-start gap-3 cursor-pointer hover:border-amber-500/50">
                          <input
                            type="checkbox"
                            data-testid={`gma-check-${idx + 1}-${i}`}
                            checked={!!stateBucket[i]}
                            onChange={() => onToggle(i)}
                            className="w-4 h-4 accent-amber-500 mt-0.5"
                          />
                          <span className={`text-sm ${stateBucket[i] ? "text-stone-500 line-through" : "text-stone-200"}`}>
                            {label}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {step.after && (
                  <div className="mt-5 paper p-4 border-amber-500/30 bg-amber-500/5 text-amber-100 text-sm leading-relaxed whitespace-pre-line">
                    {step.after}
                  </div>
                )}
                {step.crmLink && (
                  <div className="mt-4">
                    <Link
                      to="/crm"
                      data-testid="gma-add-contact-to-crm"
                      className="pill-btn !py-2 !text-xs inline-flex"
                    >
                      <UserPlus size={12} /> {L.addContactToCrm}
                    </Link>
                  </div>
                )}
              </>
            )}

            {/* Step 04 subsections (headshots + showreel) */}
            {step.subsections && (
              <div className="mt-5 space-y-4">
                {step.subsections.map((s, i) => (
                  <div key={i} className="paper p-4">
                    <h3 className="serif text-lg text-amber-100">{s.heading}</h3>
                    <p className="text-stone-200 text-sm leading-relaxed whitespace-pre-line mt-2">
                      {s.body}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </motion.section>
        ))}
      </div>
    </div>
  );
}
